import { GoogleGenerativeAI } from '@google/generative-ai'
import {
    BusinessPerformanceReport,
    ProfitabilityRatioItem,
    FinancialStrengthRatio,
    ManagementEfficiencyRatio,
    OperatingCapabilityRatio,
    BalanceSheetSummary,
} from '../types'
import {
    getCachedAnalysis,
    setCachedAnalysis,
    generateCacheKey,
    createDataHash,
    clearExpiredCaches,
} from './aiCache'

export interface BusinessAnalysisRequest {
    data: BusinessPerformanceReport[]
    period_range: string
    selected_metrics: string[]
}

export interface ProfitabilityAnalysisRequest {
    data: ProfitabilityRatioItem[]
    period_range: string
    selected_metrics: string[]
}

export interface FinancialStrengthAnalysisRequest {
    data: FinancialStrengthRatio[]
    period_range: string
    selected_metrics: string[]
}

export interface ManagementEfficiencyAnalysisRequest {
    data: ManagementEfficiencyRatio[]
    period_range: string
    selected_metrics: string[]
}

export interface OperatingCapabilityAnalysisRequest {
    data: OperatingCapabilityRatio[]
    period_range: string
    selected_metrics: string[]
}

export interface BalanceSheetAnalysisRequest {
    data: BalanceSheetSummary[]
    period_range: string
    selected_metrics: string[]
}

export interface GeminiReportResponse {
    executive_summary: string // Tóm tắt điều hành
    key_insights: string[] // Các insight chính
    trends_analysis: string // Phân tích xu hướng
    risk_alerts: string[] // Cảnh báo rủi ro
    recommendations: string[] // Đề xuất hành động
    financial_health_score: number // Điểm sức khỏe tài chính (1-100)
}

const initializeGemini = () => {
    const apiKey = import.meta.env.APP_GEMINI_API_KEY
    if (!apiKey) {
        throw new Error(
            'Gemini API key not found. Please set APP_GEMINI_API_KEY environment variable.'
        )
    }

    const genAI = new GoogleGenerativeAI(apiKey)
    return genAI.getGenerativeModel({
        model: 'gemini-2.0-flash-lite',
        generationConfig: {
            responseMimeType: 'application/json',
        },
    })
}

export const analyzeBusinessPerformance = async (
    request: BusinessAnalysisRequest
): Promise<GeminiReportResponse> => {
    // Clear expired caches on each call
    clearExpiredCaches()

    // Generate cache key
    const dataHash = createDataHash(request.data)
    const cacheKey = generateCacheKey({
        type: 'business_performance',
        period_range: request.period_range,
        selected_metrics: request.selected_metrics,
        data_hash: dataHash,
    })

    // Check cache first
    const cached = getCachedAnalysis(cacheKey)
    if (cached) {
        console.log('📦 Using cached business performance analysis')
        return cached
    }

    console.log(
        '🔄 Fetching fresh business performance analysis from Gemini...'
    )

    const formatCurrency = (num: number) =>
        new Intl.NumberFormat('vi-VN', {
            style: 'currency',
            currency: 'VND',
            maximumFractionDigits: 0,
        }).format(num)

    // Prepare data summary
    const dataSummary = request.data.map((item) => ({
        month: item.report_month,
        net_revenue: formatCurrency(item.net_revenue),
        gross_profit: formatCurrency(item.gross_profit),
        operating_profit: formatCurrency(item.operating_profit),
        net_profit: formatCurrency(item.net_profit),
    }))

    const prompt = `
    Bạn là một chuyên gia phân tích tài chính doanh nghiệp với 20 năm kinh nghiệm trong lĩnh vực CFO và Financial Advisory.

    NHIỆM VỤ:
    Phân tích chi tiết dữ liệu kinh doanh theo tháng của công ty và đưa ra báo cáo phân tích chuyên sâu.

    DỮ LIỆU KINH DOANH (${request.period_range}):
    ${JSON.stringify(dataSummary, null, 2)}

    CÁC CHỈ SỐ ĐƯỢC CHỌN:
    ${request.selected_metrics.join(', ')}

    YÊU CẦU PHÂN TÍCH:
    1. **Executive Summary**: Tóm tắt tình hình kinh doanh tổng quan (2-3 câu ngắn gọn, súc tích)

    2. **Key Insights**: Tìm ra 3-5 insight quan trọng nhất từ dữ liệu:
       - Các tháng có performance đặc biệt (cao hoặc thấp)
       - Xu hướng tăng/giảm đáng chú ý
       - Mối tương quan giữa các chỉ số
       - Seasonality (nếu có)

    3. **Trends Analysis**: Phân tích chi tiết xu hướng:
       - Growth trajectory (tăng trưởng dương/âm)
       - Volatility (độ biến động)
       - Momentum (đà tăng/giảm)
       - So sánh performance giữa các khoảng thời gian

    4. **Risk Alerts**: Cảnh báo các vấn đề tiềm ẩn:
       - Doanh thu giảm liên tục
       - Profit margin co hẹp
       - Operating efficiency giảm
       - Cash flow issues (nếu phát hiện)

    5. **Recommendations**: Đề xuất 3-5 hành động cụ thể:
       - Ưu tiên cao nhất
       - Có thể thực hiện ngay
       - Có impact rõ ràng đến business
       - Kèm theo expected outcome

    6. **Financial Health Score**: Cho điểm từ 1-100 dựa trên:
       - Profitability trends
       - Growth consistency
       - Revenue stability
       - Overall financial position

    Trả về JSON với format:
    {
        "executive_summary": "Tóm tắt ngắn gọn...",
        "key_insights": ["Insight 1", "Insight 2", "Insight 3"],
        "trends_analysis": "Phân tích xu hướng chi tiết...",
        "risk_alerts": ["Risk 1", "Risk 2"],
        "recommendations": ["Action 1", "Action 2", "Action 3"],
        "financial_health_score": 85
    }

    LƯU Ý:
    - Sử dụng tiếng Việt chuyên nghiệp
    - Tập trung vào actionable insights
    - Đưa ra số liệu cụ thể khi phân tích
    - Recommendations phải rõ ràng, có thể thực hiện được
    `

    try {
        const model = initializeGemini()
        const result = await model.generateContent(prompt)
        const response = await result.response
        const text = response.text()

        const report = JSON.parse(text)

        // Cache the result
        setCachedAnalysis(cacheKey, report)

        return report
    } catch (error) {
        console.error('Gemini analysis failed:', error)
        throw error
    }
}

export const analyzeProfitability = async (
    request: ProfitabilityAnalysisRequest
): Promise<GeminiReportResponse> => {
    // Clear expired caches on each call
    clearExpiredCaches()

    // Generate cache key
    const dataHash = createDataHash(request.data)
    const cacheKey = generateCacheKey({
        type: 'profitability',
        period_range: request.period_range,
        selected_metrics: request.selected_metrics,
        data_hash: dataHash,
    })

    // Check cache first
    const cached = getCachedAnalysis(cacheKey)
    if (cached) {
        console.log('📦 Using cached profitability analysis')
        return cached
    }

    console.log('🔄 Fetching fresh profitability analysis from Gemini...')

    const formatPercent = (num: number) => `${num.toFixed(2)}%`

    // Prepare data summary
    const dataSummary = request.data.map((item) => ({
        month: item.report_month,
        gross_profit_margin: formatPercent(item.gross_profit_margin),
        operating_profit_margin: formatPercent(item.operating_profit_margin),
        net_profit_margin: formatPercent(item.net_profit_margin),
        other_income_margin: formatPercent(item.other_income_margin),
    }))

    const prompt = `
    Bạn là một chuyên gia phân tích tài chính doanh nghiệp với 20 năm kinh nghiệm trong lĩnh vực CFO và Financial Advisory.

    NHIỆM VỤ:
    Phân tích chi tiết các tỷ lệ sinh lợi (profitability ratios) của công ty và đưa ra báo cáo đánh giá hiệu quả hoạt động.

    DỮ LIỆU TỶ LỆ SINH LỢI (${request.period_range}):
    ${JSON.stringify(dataSummary, null, 2)}

    CÁC CHỈ SỐ ĐƯỢC CHỌN:
    ${request.selected_metrics.join(', ')}

    YÊU CẦU PHÂN TÍCH:
    1. **Executive Summary**: Tóm tắt tình hình khả năng sinh lợi (2-3 câu)

    2. **Key Insights**: Phát hiện 3-5 insight quan trọng:
       - Margin trends (tăng/giảm)
       - So sánh các loại margins
       - Tháng có performance nổi bật
       - Efficiency patterns

    3. **Trends Analysis**: Phân tích xu hướng margins:
       - Gross margin stability
       - Operating efficiency
       - Net profitability trends
       - Cost structure insights

    4. **Risk Alerts**: Cảnh báo về:
       - Margins co hẹp liên tục
       - Operating efficiency giảm
       - Cost pressure tăng
       - Competitive pressure

    5. **Recommendations**: Đề xuất cải thiện profitability:
       - Cost optimization opportunities
       - Pricing strategy adjustments
       - Operational efficiency improvements
       - Revenue mix optimization

    6. **Financial Health Score**: Điểm từ 1-100 dựa trên:
       - Margin levels vs industry benchmarks
       - Margin stability
       - Profitability trends
       - Overall financial efficiency

    Trả về JSON với format:
    {
        "executive_summary": "Tóm tắt...",
        "key_insights": ["Insight 1", "Insight 2"],
        "trends_analysis": "Phân tích chi tiết...",
        "risk_alerts": ["Risk 1", "Risk 2"],
        "recommendations": ["Action 1", "Action 2"],
        "financial_health_score": 85
    }

    LƯU Ý:
    - Sử dụng tiếng Việt chuyên nghiệp
    - So sánh với industry benchmarks khi có thể
    - Phân tích mối liên hệ giữa các margins
    - Recommendations phải cụ thể và có thể thực hiện
    `

    try {
        const model = initializeGemini()
        const result = await model.generateContent(prompt)
        const response = await result.response
        const text = response.text()

        const report = JSON.parse(text)

        // Cache the result
        setCachedAnalysis(cacheKey, report)

        return report
    } catch (error) {
        console.error('Gemini analysis failed:', error)
        throw error
    }
}

export const analyzeFinancialStrength = async (
    request: FinancialStrengthAnalysisRequest
): Promise<GeminiReportResponse> => {
    clearExpiredCaches()

    const dataHash = createDataHash(request.data)
    const cacheKey = generateCacheKey({
        type: 'financial_strength',
        period_range: request.period_range,
        selected_metrics: request.selected_metrics,
        data_hash: dataHash,
    })

    const cached = getCachedAnalysis(cacheKey)
    if (cached) {
        console.log('📦 Using cached financial strength analysis')
        return cached
    }

    console.log('🔄 Fetching fresh financial strength analysis from Gemini...')

    const formatRatio = (num: number) => num.toFixed(2)

    const dataSummary = request.data.map((item) => ({
        month: item.report_month,
        current_ratio: formatRatio(item.current_ratio),
        quick_ratio: formatRatio(item.quick_ratio),
        interest_coverage: formatRatio(item.interest_coverage),
        debt_to_equity_ratio: formatRatio(item.debt_to_equity_ratio),
    }))

    const prompt = `
    Bạn là một chuyên gia phân tích tài chính doanh nghiệp với 20 năm kinh nghiệm trong lĩnh vực CFO và Financial Advisory.

    NHIỆM VỤ:
    Phân tích chi tiết các chỉ số sức khỏe tài chính (Financial Strength Ratios) của công ty và đưa ra báo cáo đánh giá khả năng thanh toán và cấu trúc vốn.

    DỮ LIỆU CHỈ SỐ SỨC KHỎE TÀI CHÍNH (${request.period_range}):
    ${JSON.stringify(dataSummary, null, 2)}

    CÁC CHỈ SỐ ĐƯỢC CHỌN:
    ${request.selected_metrics.join(', ')}

    GIẢI THÍCH CHỈ SỐ:
    - Current Ratio (Tỷ số thanh toán hiện hành): Tài sản ngắn hạn / Nợ ngắn hạn - Tốt nếu > 1.5
    - Quick Ratio (Tỷ số thanh toán nhanh): (Tài sản ngắn hạn - Hàng tồn kho) / Nợ ngắn hạn - Tốt nếu > 1.0
    - Interest Coverage (Khả năng thanh toán lãi vay): EBIT / Chi phí lãi vay - Tốt nếu > 3.0
    - Debt to Equity Ratio (Tỷ lệ nợ trên vốn): Tổng nợ / Vốn chủ sở hữu - Tốt nếu < 2.0

    YÊU CẦU PHÂN TÍCH:
    1. **Executive Summary**: Tóm tắt tình hình sức khỏe tài chính tổng quan (2-3 câu)

    2. **Key Insights**: Phát hiện 3-5 insight quan trọng:
       - Khả năng thanh toán ngắn hạn (liquidity)
       - Khả năng thanh toán nhanh (quick liquidity)
       - Khả năng đáp ứng nghĩa vụ lãi vay
       - Cấu trúc vốn và đòn bẩy tài chính
       - Xu hướng thay đổi các tỷ lệ

    3. **Trends Analysis**: Phân tích xu hướng:
       - Liquidity trends (cải thiện/xấu đi)
       - Solvency position (khả năng thanh toán dài hạn)
       - Financial leverage changes
       - Interest payment capacity
       - So sánh với ngưỡng an toàn

    4. **Risk Alerts**: Cảnh báo rủi ro:
       - Current/Quick ratio thấp hơn ngưỡng an toàn
       - Interest coverage yếu
       - Debt to equity ratio cao
       - Xu hướng xấu đi liên tục
       - Rủi ro thanh khoản

    5. **Recommendations**: Đề xuất cải thiện:
       - Tăng cường khả năng thanh toán
       - Tối ưu hóa cấu trúc vốn
       - Quản lý nợ hiệu quả
       - Cải thiện dòng tiền
       - Chiến lược tài trợ

    6. **Financial Health Score**: Điểm từ 1-100 dựa trên:
       - Liquidity position
       - Solvency strength
       - Debt management
       - Overall financial stability

    Trả về JSON với format:
    {
        "executive_summary": "Tóm tắt...",
        "key_insights": ["Insight 1", "Insight 2"],
        "trends_analysis": "Phân tích chi tiết...",
        "risk_alerts": ["Risk 1", "Risk 2"],
        "recommendations": ["Action 1", "Action 2"],
        "financial_health_score": 85
    }

    LƯU Ý:
    - Sử dụng tiếng Việt chuyên nghiệp
    - So sánh với ngưỡng chuẩn ngành
    - Đánh giá mức độ rủi ro cụ thể
    - Recommendations phải thực tế và có thể thực hiện
    `

    try {
        const model = initializeGemini()
        const result = await model.generateContent(prompt)
        const response = await result.response
        const text = response.text()

        const report = JSON.parse(text)

        setCachedAnalysis(cacheKey, report)

        return report
    } catch (error) {
        console.error('Gemini analysis failed:', error)
        throw error
    }
}

export const analyzeManagementEfficiency = async (
    request: ManagementEfficiencyAnalysisRequest
): Promise<GeminiReportResponse> => {
    clearExpiredCaches()

    const dataHash = createDataHash(request.data)
    const cacheKey = generateCacheKey({
        type: 'management_efficiency',
        period_range: request.period_range,
        selected_metrics: request.selected_metrics,
        data_hash: dataHash,
    })

    const cached = getCachedAnalysis(cacheKey)
    if (cached) {
        console.log('📦 Using cached management efficiency analysis')
        return cached
    }

    console.log(
        '🔄 Fetching fresh management efficiency analysis from Gemini...'
    )

    const formatPercent = (num: number) => `${num.toFixed(2)}%`

    const dataSummary = request.data.map((item) => ({
        month: item.report_month,
        roa: formatPercent(item.roa),
        roe: formatPercent(item.roe),
        roic: formatPercent(item.roic),
        roce: formatPercent(item.roce),
    }))

    const prompt = `
    Bạn là một chuyên gia phân tích tài chính doanh nghiệp với 20 năm kinh nghiệm trong lĩnh vực CFO và Financial Advisory.

    NHIỆM VỤ:
    Phân tích chi tiết các chỉ số hiệu quả quản lý (Management Efficiency Ratios) của công ty và đưa ra báo cáo đánh giá khả năng sinh lợi từ tài sản và vốn.

    DỮ LIỆU CHỈ SỐ HIỆU QUẢ QUẢN LÝ (${request.period_range}):
    ${JSON.stringify(dataSummary, null, 2)}

    CÁC CHỈ SỐ ĐƯỢC CHỌN:
    ${request.selected_metrics.join(', ')}

    GIẢI THÍCH CHỈ SỐ:
    - ROA (Return on Assets): Lợi nhuận ròng / Tổng tài sản - Đo lường hiệu quả sử dụng tài sản
    - ROE (Return on Equity): Lợi nhuận ròng / Vốn chủ sở hữu - Đo lường lợi nhuận cho cổ đông
    - ROIC (Return on Invested Capital): NOPAT / Vốn đầu tư - Đo lường hiệu quả sử dụng vốn
    - ROCE (Return on Capital Employed): EBIT / Vốn sử dụng - Đo lường hiệu quả vốn hoạt động

    YÊU CẦU PHÂN TÍCH:
    1. **Executive Summary**: Tóm tắt hiệu quả quản lý tài sản và vốn (2-3 câu)

    2. **Key Insights**: Phát hiện 3-5 insight quan trọng:
       - Asset utilization efficiency (ROA trends)
       - Shareholder value creation (ROE performance)
       - Capital deployment effectiveness (ROIC/ROCE)
       - So sánh giữa các chỉ số ROA, ROE, ROIC, ROCE
       - Tháng có hiệu quả cao/thấp

    3. **Trends Analysis**: Phân tích xu hướng:
       - ROA trajectory và asset productivity
       - ROE trends và shareholder returns
       - ROIC vs ROCE comparison
       - Capital efficiency evolution
       - Management effectiveness over time

    4. **Risk Alerts**: Cảnh báo vấn đề:
       - ROA/ROE giảm liên tục
       - ROIC thấp hơn cost of capital
       - Asset productivity declining
       - Poor capital allocation
       - Inefficient resource utilization

    5. **Recommendations**: Đề xuất cải thiện:
       - Asset optimization strategies
       - Capital allocation improvements
       - Operational efficiency initiatives
       - Cost structure optimization
       - Value creation opportunities

    6. **Financial Health Score**: Điểm từ 1-100 dựa trên:
       - ROA, ROE, ROIC, ROCE levels
       - Return consistency
       - Trend direction
       - Management effectiveness

    Trả về JSON với format:
    {
        "executive_summary": "Tóm tắt...",
        "key_insights": ["Insight 1", "Insight 2"],
        "trends_analysis": "Phân tích chi tiết...",
        "risk_alerts": ["Risk 1", "Risk 2"],
        "recommendations": ["Action 1", "Action 2"],
        "financial_health_score": 85
    }

    LƯU Ý:
    - Sử dụng tiếng Việt chuyên nghiệp
    - So sánh các chỉ số ROA, ROE, ROIC, ROCE với nhau
    - Phân tích mối quan hệ giữa leverage và returns
    - Recommendations phải cụ thể và actionable
    `

    try {
        const model = initializeGemini()
        const result = await model.generateContent(prompt)
        const response = await result.response
        const text = response.text()

        const report = JSON.parse(text)

        setCachedAnalysis(cacheKey, report)

        return report
    } catch (error) {
        console.error('Gemini analysis failed:', error)
        throw error
    }
}

export const analyzeOperatingCapability = async (
    request: OperatingCapabilityAnalysisRequest
): Promise<GeminiReportResponse> => {
    clearExpiredCaches()

    const dataHash = createDataHash(request.data)
    const cacheKey = generateCacheKey({
        type: 'operating_capability',
        period_range: request.period_range,
        selected_metrics: request.selected_metrics,
        data_hash: dataHash,
    })

    const cached = getCachedAnalysis(cacheKey)
    if (cached) {
        console.log('📦 Using cached operating capability analysis')
        return cached
    }

    console.log(
        '🔄 Fetching fresh operating capability analysis from Gemini...'
    )

    const formatTurnover = (num: number) => `${num.toFixed(2)}x`

    const dataSummary = request.data.map((item) => ({
        month: item.report_month,
        total_asset_turnover: formatTurnover(item.total_asset_turnover),
        inventory_turnover: formatTurnover(item.inventory_turnover),
        receivables_turnover: formatTurnover(item.receivables_turnover),
        current_asset_turnover: formatTurnover(item.current_asset_turnover),
    }))

    const prompt = `
    Bạn là một chuyên gia phân tích tài chính doanh nghiệp với 20 năm kinh nghiệm trong lĩnh vực CFO và Financial Advisory.

    NHIỆM VỤ:
    Phân tích chi tiết các chỉ số năng lực hoạt động (Operating Capability Ratios) của công ty và đưa ra báo cáo đánh giá hiệu quả sử dụng tài sản và quản lý chu kỳ kinh doanh.

    DỮ LIỆU CHỈ SỐ NĂNG LỰC HOẠT ĐỘNG (${request.period_range}):
    ${JSON.stringify(dataSummary, null, 2)}

    CÁC CHỈ SỐ ĐƯỢC CHỌN:
    ${request.selected_metrics.join(', ')}

    GIẢI THÍCH CHỈ SỐ:
    - Total Asset Turnover: Doanh thu / Tổng tài sản - Đo lường hiệu quả tổng thể
    - Inventory Turnover: Giá vốn / Hàng tồn kho - Tốc độ luân chuyển hàng tồn kho
    - Receivables Turnover: Doanh thu / Khoản phải thu - Hiệu quả thu hồi công nợ
    - Current Asset Turnover: Doanh thu / Tài sản ngắn hạn - Hiệu quả tài sản ngắn hạn

    YÊU CẦU PHÂN TÍCH:
    1. **Executive Summary**: Tóm tắt năng lực hoạt động tổng quan (2-3 câu)

    2. **Key Insights**: Phát hiện 3-5 insight quan trọng:
       - Asset utilization efficiency
       - Inventory management effectiveness
       - Working capital efficiency
       - Collection efficiency (receivables)
       - Operating cycle performance

    3. **Trends Analysis**: Phân tích xu hướng:
       - Asset turnover trends
       - Inventory velocity changes
       - Collection period evolution
       - Working capital cycle
       - Operational efficiency patterns

    4. **Risk Alerts**: Cảnh báo vấn đề:
       - Asset turnover giảm
       - Inventory buildup (hàng tồn kho tăng)
       - Receivables collection slowdown
       - Working capital inefficiency
       - Operating cycle lengthening

    5. **Recommendations**: Đề xuất cải thiện:
       - Asset utilization improvements
       - Inventory optimization strategies
       - Receivables collection acceleration
       - Working capital management
       - Operating cycle reduction

    6. **Financial Health Score**: Điểm từ 1-100 dựa trên:
       - Turnover ratio levels
       - Efficiency trends
       - Working capital management
       - Operational effectiveness

    Trả về JSON với format:
    {
        "executive_summary": "Tóm tắt...",
        "key_insights": ["Insight 1", "Insight 2"],
        "trends_analysis": "Phân tích chi tiết...",
        "risk_alerts": ["Risk 1", "Risk 2"],
        "recommendations": ["Action 1", "Action 2"],
        "financial_health_score": 85
    }

    LƯU Ý:
    - Sử dụng tiếng Việt chuyên nghiệp
    - So sánh turnover ratios với industry benchmarks
    - Phân tích cash conversion cycle
    - Recommendations phải cụ thể và có impact rõ ràng
    `

    try {
        const model = initializeGemini()
        const result = await model.generateContent(prompt)
        const response = await result.response
        const text = response.text()

        const report = JSON.parse(text)

        setCachedAnalysis(cacheKey, report)

        return report
    } catch (error) {
        console.error('Gemini analysis failed:', error)
        throw error
    }
}

export const analyzeBalanceSheet = async (
    request: BalanceSheetAnalysisRequest
): Promise<GeminiReportResponse> => {
    clearExpiredCaches()

    const dataHash = createDataHash(request.data)
    const cacheKey = generateCacheKey({
        type: 'balance_sheet',
        period_range: request.period_range,
        selected_metrics: request.selected_metrics,
        data_hash: dataHash,
    })

    const cached = getCachedAnalysis(cacheKey)
    if (cached) {
        console.log('📦 Using cached balance sheet analysis')
        return cached
    }

    console.log('🔄 Fetching fresh balance sheet analysis from Gemini...')

    const formatCurrency = (num: number) =>
        new Intl.NumberFormat('vi-VN', {
            style: 'currency',
            currency: 'VND',
            maximumFractionDigits: 0,
        }).format(num)

    const dataSummary = request.data.map((item) => ({
        month: item.report_month,
        total_assets: formatCurrency(item.total_assets),
        total_liability: formatCurrency(item.total_liability),
        total_equity: formatCurrency(item.total_equity),
        is_balanced: item.is_balanced,
        variance: formatCurrency(item.variance),
    }))

    const prompt = `
    Bạn là một chuyên gia phân tích tài chính doanh nghiệp với 20 năm kinh nghiệm trong lĩnh vực CFO và Financial Advisory.

    NHIỆM VỤ:
    Phân tích chi tiết Bảng Cân Đối Kế Toán (Balance Sheet) của công ty và đưa ra báo cáo đánh giá về cấu trúc tài sản, nguồn vốn và tình hình tài chính tổng thể.

    DỮ LIỆU CÂN ĐỐI KẾ TOÁN (${request.period_range}):
    ${JSON.stringify(dataSummary, null, 2)}

    CÁC CHỈ SỐ ĐƯỢC CHỌN:
    ${request.selected_metrics.join(', ')}

    GIẢI THÍCH CÁC THÀNH PHẦN:
    - Total Assets (Tổng Tài Sản): Toàn bộ tài sản công ty sở hữu
    - Total Liability (Tổng Nợ Phải Trả): Toàn bộ nghĩa vụ nợ của công ty
    - Total Equity (Vốn Chủ Sở Hữu): Tài sản ròng sau khi trừ nợ
    - Is Balanced: Kiểm tra phương trình kế toán (Assets = Liabilities + Equity)
    - Variance: Chênh lệch nếu không cân bằng

    NGUYÊN TẮC CƠ BẢN:
    - Phương trình kế toán: Tài sản = Nợ phải trả + Vốn chủ sở hữu
    - Equity ratio cao → Cấu trúc vốn lành mạnh
    - Liability ratio cao → Rủi ro tài chính cao
    - Asset growth → Đầu tư mở rộng hoặc tăng trưởng

    YÊU CẦU PHÂN TÍCH:
    1. **Executive Summary**: Tóm tắt tình hình cân đối kế toán tổng quan (2-3 câu)

    2. **Key Insights**: Phát hiện 3-5 insight quan trọng:
       - Cơ cấu tài sản (asset composition)
       - Cơ cấu nguồn vốn (financing structure)
       - Tỷ lệ nợ/vốn chủ sở hữu (debt-to-equity)
       - Xu hướng tăng trưởng assets/equity/liability
       - Sự cân bằng và stability của balance sheet

    3. **Trends Analysis**: Phân tích xu hướng:
       - Asset growth trajectory
       - Liability trend (tăng/giảm)
       - Equity evolution
       - Capital structure changes
       - Debt-to-equity ratio trends
       - Financial leverage patterns

    4. **Risk Alerts**: Cảnh báo rủi ro:
       - Balance sheet không cân bằng (variance != 0)
       - Debt ratio quá cao (>70%)
       - Equity giảm liên tục
       - Asset không tăng trưởng
       - Working capital issues
       - Overleveraged position

    5. **Recommendations**: Đề xuất cải thiện:
       - Tối ưu hóa cấu trúc vốn
       - Quản lý nợ hiệu quả
       - Tăng cường equity base
       - Asset optimization strategies
       - Working capital management
       - Debt restructuring (nếu cần)

    6. **Financial Health Score**: Điểm từ 1-100 dựa trên:
       - Asset quality và growth
       - Capital structure stability
       - Debt management effectiveness
       - Equity strength
       - Overall balance sheet health

    Trả về JSON với format:
    {
        "executive_summary": "Tóm tắt...",
        "key_insights": ["Insight 1", "Insight 2"],
        "trends_analysis": "Phân tích chi tiết...",
        "risk_alerts": ["Risk 1", "Risk 2"],
        "recommendations": ["Action 1", "Action 2"],
        "financial_health_score": 85
    }

    LƯU Ý:
    - Sử dụng tiếng Việt chuyên nghiệp
    - Phân tích mối quan hệ giữa Assets, Liabilities, Equity
    - Đánh giá financial leverage và capital structure
    - So sánh với chuẩn mực ngành khi có thể
    - Recommendations phải cụ thể và actionable
    - Chú ý đến variance nếu balance sheet không cân bằng
    `

    try {
        const model = initializeGemini()
        const result = await model.generateContent(prompt)
        const response = await result.response
        const text = response.text()

        const report = JSON.parse(text)

        setCachedAnalysis(cacheKey, report)

        return report
    } catch (error) {
        console.error('Gemini analysis failed:', error)
        throw error
    }
}
